import { Mic, MicOff, Send, Settings, Square, Volume2, VolumeX, Wifi } from "lucide-react";
import { useState } from "react";
import { CharacterStage } from "../components/CharacterStage";
import { MemoryCandidates } from "../components/MemoryCandidates";
import { nowIso } from "../constants";
import { primeSpeechSynthesis, speakText, stopSpeech } from "../audio/speech";
import { buildPromptPayload } from "../memory/promptBuilder";
import { recentConversation, selectRelevantMemories } from "../memory/selectMemories";
import { streamChat } from "../openai/chatClient";
import { connectRealtime, type RealtimeConnection } from "../realtime/realtimeClient";
import { getMessagesByConversation, putRecord, upsertConversation } from "../storage/db";
import type { AppSettings, CharacterState, MediaRecord, MemoryCandidate, MemoryItem, Message, VoiceProfile } from "../types/app";
import { createId } from "../utils/id";
import { useSpeechRecognition } from "../hooks/useSpeechRecognition";
import { resumeAudioContext } from "../audio/webAudioChain";

interface Props {
  settings: AppSettings;
  memories: MemoryItem[];
  messages: Message[];
  media?: MediaRecord;
  voiceProfile?: VoiceProfile;
  onSettings: (patch: Partial<AppSettings>) => Promise<void>;
  onRefresh: () => Promise<void>;
  onError: (message: string) => void;
  openSettings: () => void;
}

export function ConversationPage({ settings, memories, messages, media, voiceProfile, onSettings, onRefresh, onError, openSettings }: Props) {
  const [text, setText] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [state, setState] = useState<CharacterState>("idle");
  const [status, setStatus] = useState("低コストモード");
  const [candidates, setCandidates] = useState<MemoryCandidate[]>([]);
  const [connection, setConnection] = useState<RealtimeConnection | null>(null);
  const recognition = useSpeechRecognition(
    (recognized) => {
      setText(recognized);
      void sendText(recognized);
    },
    () => onError("音声認識非対応です。テキスト入力へ切り替えて続行できます。"),
  );

  async function sendText(input = text) {
    const userMessage = input.trim();
    if (!userMessage) return;
    setText("");
    setState("listening");
    const conversationId = messages[0]?.conversationId ?? createId("conversation");
    if (!messages[0]) await upsertConversation({ id: conversationId, title: userMessage.slice(0, 24), createdAt: nowIso(), updatedAt: nowIso() });
    const user: Message = { id: createId("message"), conversationId, role: "user", content: userMessage, createdAt: nowIso() };
    await putRecord("messages", user);
    const recent = await getMessagesByConversation(conversationId, 10);
    const selected = selectRelevantMemories(memories, userMessage);
    setSubtitle("");
    setState("speaking");
    try {
      const result = await streamChat(
        buildPromptPayload({
          conversationId,
          characterName: settings.characterName,
          systemPrompt: settings.systemPrompt,
          memories: selected.map((item) => item.content),
          recentMessages: recentConversation(recent),
          userMessage,
          maxResponseChars: settings.maxResponseChars,
        }),
        (delta) => setSubtitle((previous) => previous + delta),
      );
      const assistant: Message = { id: createId("message"), conversationId, role: "assistant", content: result.text, createdAt: nowIso() };
      await putRecord("messages", assistant);
      setCandidates(result.memoryCandidates);
      if (!settings.muted) {
        const played = await speakText(result.text, settings.selectedVoice, voiceProfile);
        if (!played) onError("音声合成を開始できませんでした。音声設定のテスト再生を試してください。テキスト会話は続けられます。");
      }
    } catch (error) {
      onError(error instanceof Error ? error.message : "ネットワークに接続できないか、APIエラーが発生しました。");
    } finally {
      setState("idle");
      await onRefresh();
    }
  }

  async function toggleLive() {
    if (connection) {
      connection.disconnect();
      setConnection(null);
      setStatus("低コストモード");
      return;
    }
    if (!settings.liveModeEnabled) {
      onError("Liveモードは設定画面で明示的に有効化してください。");
      return;
    }
    try {
      await resumeAudioContext();
      const live = await connectRealtime(
        voiceProfile ?? {
          id: "temp",
          name: "temp",
          sourceName: "",
          sourceLicense: "",
          pitch: 1,
          rate: 1,
          gain: 1,
          warmth: 0.5,
          brightness: 0.5,
          softness: 0.5,
          breathiness: 0.2,
          compression: 0.4,
          reverb: 0,
          pauseLength: 0,
          createdAt: nowIso(),
          updatedAt: nowIso(),
        },
        {
          onStatus: setStatus,
          onError,
          onAssistantText: (delta) => setSubtitle((previous) => previous + delta),
          onSpeaking: (speaking) => setState(speaking ? "speaking" : "idle"),
        },
        settings.maxConversationMinutes,
      );
      setConnection(live);
    } catch (error) {
      onError(error instanceof Error ? error.message : "Realtime接続失敗です。低コストモードへ切り替えます。");
      setStatus("低コストモード");
      setConnection(null);
      await onSettings({ selectedMode: "low-cost" });
    }
  }

  return (
    <main className="conversation screen">
      <div className="topbar">
        <span><Wifi size={16} /> {connection ? "Live" : settings.selectedMode === "live" ? "Live待機" : "低コスト"}</span>
        <span>{status}</span>
        <button type="button" aria-label="設定" onClick={openSettings}><Settings /></button>
      </div>
      <CharacterStage media={media} state={recognition.listening ? "listening" : state} name={settings.characterName} />
      <section className="subtitle" aria-live="polite">{subtitle || "話しかけるか、下に入力してください。"}</section>
      <div className="controls">
        <button type="button" data-testid="mic-button" aria-label="マイク" onClick={() => {
          primeSpeechSynthesis();
          if (recognition.listening) recognition.stop();
          else recognition.start();
        }}>
          {recognition.listening ? <MicOff /> : <Mic />}
        </button>
        <button type="button" onClick={toggleLive}>{connection ? "Live終了" : "Live開始"}</button>
        <button type="button" aria-label="AI発話停止" onClick={() => { stopSpeech(); connection?.stopAssistant(); setState("idle"); }}><Square /></button>
        <button
          type="button"
          aria-label={settings.muted ? "ミュート解除" : "ミュート"}
          aria-pressed={settings.muted}
          onClick={() => void onSettings({ muted: !settings.muted })}
        >
          {settings.muted ? <VolumeX /> : <Volume2 />}
        </button>
      </div>
      <form className="composer" onSubmit={(event) => {
        event.preventDefault();
        primeSpeechSynthesis();
        void sendText();
      }}>
        <input data-testid="chat-input" value={text} onChange={(event) => setText(event.target.value)} placeholder="テキストでも話せます" />
        <button data-testid="send-button" type="submit" aria-label="送信"><Send /></button>
      </form>
      <MemoryCandidates
        candidates={candidates}
        onSave={async (content, temporary) => {
          await putRecord("memories", {
            id: createId("memory"),
            category: temporary ? "temporary" : "normal",
            content,
            importance: 3,
            isPinned: false,
            createdAt: nowIso(),
            updatedAt: nowIso(),
          });
          await onRefresh();
        }}
      />
    </main>
  );
}
